import { Center, type CenterProps } from '@react-three/drei'
import { type ThreeElements } from '@react-three/fiber'
import { useMemo, useState } from 'react'
import { MathUtils } from 'three'

import { LocomotiveModel, WagonAModel } from '@components/models'
import { useIsTouch } from '@hooks'
import { useGameStore } from '@stores'
import { WoodenSignBillboard } from './WoodenSignBillboard'
import { WoodenSignButton } from './WoodenSignButton'

interface TrainPreviewProps extends CenterProps {
  goal: string[]
}

export function TrainPreview({ goal, ...props }: TrainPreviewProps) {
  const [dirSign] = useState(() => useGameStore.getState().network.locomotive?.direction ?? 1)

  return (
    <Center scale-z={dirSign} rotation-y={MathUtils.degToRad(90)} {...props}>
      <LocomotiveModel />
      {goal.map((color, i) => (
        <WagonAModel key={i} color={color} position-z={-(i + 1) * 2.7} />
      ))}
    </Center>
  )
}

export function Goal(props: ThreeElements['group']) {
  const isTouch = useIsTouch()

  const goal = useGameStore(state => state.level.goal.sequence || [])
  const currentLevelIndex = useGameStore(state => state.currentLevelIndex || 0)
  const messageDesktop = useGameStore(state => state.level.goal.message)
  const messageTouch = useGameStore(state => state.level.goal.messageTouch)
  const levelComplete = useGameStore(state => state.levelComplete)
  const setNextLevelIndex = useGameStore(state => state.setNextLevelIndex)
  const resetLevel = useGameStore(state => state.resetLevel)

  const renderTextureKey = useMemo(() => goal.join(','), [goal])
  const message = isTouch ? messageTouch : messageDesktop

  return (
    <group {...props}>
      <group position={[-2.5, 0, 0.5]} rotation-y={MathUtils.degToRad(15)}>
        <WoodenSignButton label="⟲" icon disabled={levelComplete} onClick={resetLevel} />
        <WoodenSignButton
          secondary
          label="◀"
          icon
          disabled={levelComplete}
          onClick={() => setNextLevelIndex(1, true)}
        />
      </group>

      <WoodenSignBillboard
        label={message}
        level={currentLevelIndex - 1}
        labelProps={{ maxWidth: 15, position: [0, goal.length > 0 ? 1.2 : 0, 0] }}
        renderTextureKey={renderTextureKey}
      >
        {goal.length > 0 && <TrainPreview goal={goal} position-y={message ? -1.2 : 0} />}
      </WoodenSignBillboard>
    </group>
  )
}
